import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Search } from 'lucide-react';
import { graphql } from '@/gql';
import type { VoucherValidityMode } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Select } from '@/components/ui/Select';
import { useDebounced } from '@/hooks/useDebounced';
import { cn } from '@/lib/cn';
import { formatDateTimeAmsterdam } from '@/lib/format';
import { voucherValidityModeLabel } from '@/lib/labels';
import { VALIDITY_MODES, validityModeNeedsEndDate, type RuleNight } from './ticketClaimedRule';

const VoucherRuleNightsDoc = graphql(/* GraphQL */ `
  query VoucherRuleNights($search: String, $limit: Int) {
    voucherRuleNights(search: $search, limit: $limit) {
      id
      title
      startsAt
      scope {
        id
        name
      }
      stages {
        id
        name
        isActive
      }
    }
  }
`);

const VALIDITY_HELP: Record<VoucherValidityMode, string> = {
  AFTER_CLAIM:
    'Usable from the moment the ticket is claimed until the end of the “Valid until” trading day (06:00 the next morning).',
  NIGHT:
    'Usable only during the night’s own trading day. No end date.',
  AFTER_NIGHT:
    'Usable from the end of the night’s night until the end of the “Valid until” trading day (06:00 the next morning).',
};

export interface TicketClaimedRuleFieldsProps {
  night: RuleNight | null;
  onNightChange: (next: RuleNight | null) => void;
  stageIds: string[];
  onStageIdsChange: (next: string[]) => void;
  validityMode: VoucherValidityMode | '';
  onValidityModeChange: (next: VoucherValidityMode | '') => void;
  validUntil: string;
  onValidUntilChange: (next: string) => void;
  /** Name of the venue the rule lives in, to tell this venue's nights apart from another venue's. */
  activeScopeName?: string;
  /** The rule points at a night in a venue the viewer cannot read: show it read-only. */
  hiddenNight?: boolean;
  onReplaceHidden?: () => void;
}

/** Fields that only exist for the Ticket claimed trigger. */
export function TicketClaimedRuleFields({
  night,
  onNightChange,
  stageIds,
  onStageIdsChange,
  validityMode,
  onValidityModeChange,
  validUntil,
  onValidUntilChange,
  activeScopeName,
  hiddenNight,
  onReplaceHidden,
}: TicketClaimedRuleFieldsProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim(), 250);
  const { data, previousData, loading, error } = useQuery(VoucherRuleNightsDoc, {
    variables: { search: debouncedSearch || null, limit: 50 },
    errorPolicy: 'all',
  });
  const results = (data ?? previousData)?.voucherRuleNights ?? [];

  const toggleStage = (id: string, checked: boolean) => {
    onStageIdsChange(checked ? [...stageIds, id] : stageIds.filter((s) => s !== id));
  };

  return (
    <>
      <FormField
        label="Night"
        required
        description={
          hiddenNight
            ? undefined
            : 'Active nights in this venue and in any other venue where you can read tickets. The venue name is shown so a night run elsewhere is recognisable. The voucher is still minted in this venue.'
        }
      >
        {hiddenNight ? (
          <div
            id="admin.vouchers.ruleNightHidden"
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
          >
            <span className="text-gray-500 dark:text-gray-400">Night you cannot view</span>
            <button
              type="button"
              id="admin.vouchers.ruleNightClear"
              className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
              onClick={onReplaceHidden}
            >
              Change
            </button>
          </div>
        ) : (
        <div className="space-y-2">
          {night ? (
            <div
              id="admin.vouchers.ruleNight"
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-300 bg-brand-50 px-3 py-2 text-sm dark:border-brand-500/40 dark:bg-brand-500/10"
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900 dark:text-white">
                  {night.title}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  {formatDateTimeAmsterdam(night.startsAt)} · {night.scope.name}
                  {activeScopeName && night.scope.name !== activeScopeName
                    ? ' (other venue)'
                    : ''}
                </div>
              </div>
              <button
                type="button"
                id="admin.vouchers.ruleNightClear"
                className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
                onClick={() => {
                  onNightChange(null);
                  onStageIdsChange([]);
                }}
              >
                Change
              </button>
            </div>
          ) : null}
          <Input
            id="admin.vouchers.ruleNightSearch"
            aria-label="Search nights"
            placeholder="Search by title…"
            leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {error && results.length === 0 ? (
            <QueryErrorNotice error={error} title="Could not load nights" />
          ) : null}
          <ul
            aria-label="Nights"
            className="max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700"
          >
            {results.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                {loading
                  ? 'Searching…'
                  : debouncedSearch
                    ? 'No nights match that search.'
                    : 'No nights available. You need ticket access in a venue, and the night must be active.'}
              </li>
            ) : (
              results.map((p) => {
                const isSelected = night?.id === p.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      data-night-id={p.id}
                      aria-pressed={isSelected}
                      className={cn(
                        'flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800',
                        isSelected && 'bg-brand-50 dark:bg-brand-500/15',
                      )}
                      onClick={() => {
                        if (night?.id !== p.id) {
                          onStageIdsChange([]);
                        }
                        onNightChange(p);
                      }}
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{p.title}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTimeAmsterdam(p.startsAt)} · {p.scope.name}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
        )}
      </FormField>

      {hiddenNight ? (
        <FormField label="Stages">
          <p id="admin.vouchers.ruleStagesHidden" className="text-sm text-gray-500 dark:text-gray-400">
            Stages are not visible because you cannot view this night. Saving keeps the
            current night and stages unless you change the night.
          </p>
        </FormField>
      ) : null}

      {night ? (
        <FormField
          label="Stages"
          description="Only tickets of the ticked stages earn this voucher. Leave every box empty to include every stage of the night."
        >
          <div id="admin.vouchers.ruleStages" className="space-y-1.5">
            {night.stages.length === 0 ? (
              <p className="text-sm text-gray-500">This night has no stages yet.</p>
            ) : (
              night.stages.map((stage) => {
                const checked = stageIds.includes(stage.id);
                return (
                  <label
                    key={stage.id}
                    className="flex items-center gap-2 text-sm text-gray-900 dark:text-white"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!stage.isActive && !checked}
                      onChange={(e) => toggleStage(stage.id, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-500"
                    />
                    <span>{stage.name}</span>
                    {!stage.isActive ? <Badge tone="neutral">Inactive</Badge> : null}
                  </label>
                );
              })
            )}
            {stageIds.length === 0 && night.stages.length > 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No stage ticked: every stage counts.
              </p>
            ) : null}
          </div>
        </FormField>
      ) : null}

      <FormField
        label="Validity mode"
        required
        description={validityMode ? VALIDITY_HELP[validityMode] : 'When the minted voucher can be used.'}
      >
        <Select
          id="admin.vouchers.ruleValidityMode"
          value={validityMode}
          placeholder="Choose when it is valid…"
          onChange={(e) => onValidityModeChange(e.target.value as VoucherValidityMode)}
          options={VALIDITY_MODES.map((m) => ({
            value: m,
            label: voucherValidityModeLabel(m),
          }))}
        />
      </FormField>

      {validityModeNeedsEndDate(validityMode) ? (
        <FormField
          label="Valid until"
          required
          description="The last trading day the voucher can be used. It stays valid until 06:00 the next morning."
        >
          <Input
            id="admin.vouchers.ruleValidUntil"
            type="date"
            value={validUntil}
            onChange={(e) => onValidUntilChange(e.target.value)}
            required
          />
        </FormField>
      ) : null}
    </>
  );
}
